/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Cross-project localStorage cache for RCSB-fetched PDB files. Keyed by
 * uppercase PDB ID (e.g. `aims:pdb:1HVR`) so the same receptor doesn't have
 * to be re-fetched when the user bounces between projects on the same
 * target.
 *
 * The per-project cache (`projectState.protein.data`, persisted by the
 * Zustand `persist` middleware) already covers within-project reload. This
 * module fills the cross-project gap.
 *
 * Eviction: LRU by index list, bounded by BOTH a total-byte budget and an
 * entry count. localStorage is a SINGLE ~5–10 MB budget shared across the
 * whole origin — this cache, the biomolecule thumbnail cache, and every
 * project's Zustand auto-save all draw from it. An earlier 12 × 1 MB ceiling
 * meant one Protein-Lab session could consume ~12 MB and starve unrelated
 * projects' auto-saves (they'd fail with QuotaExceededError even with only a
 * handful of tiny molecules). So the hard limit here is TOTAL_BUDGET_BYTES:
 * once the newest entries would exceed it, the LRU tail is evicted. This
 * cache is a pure optimization — bioStructureViewer re-fetches from RCSB on a
 * miss — so a tight budget costs at worst one refetch, never correctness.
 */

const STORAGE_PREFIX = 'aims:pdb:';
const INDEX_KEY = 'aims:pdb:__index__';
const SCHEMA_VERSION = 1;
// Belt-and-suspenders count cap; the byte budget below is the real limit.
const MAX_ENTRIES = 8;
// Hard total budget for the whole PDB cache — kept well under the per-origin
// quota so the biomolecule thumbnail cache and every project's auto-save
// always have room. ~2.5 MB fits several typical structures (50–300 KB each)
// while leaving the majority of the quota for everything else.
const TOTAL_BUDGET_BYTES = 2_500_000;
// Per-entry soft cap — an entry larger than this is skipped entirely rather
// than allowed to swallow the whole budget on its own (a huge assembly is
// cheaper to refetch than to cache at the cost of evicting everything else).
const SOFT_PER_ENTRY_BYTES = 1_200_000;

interface CachedPdb {
  v: number;
  id: string;
  text: string;
  ts: number; // last access (read OR write) — drives LRU
}

const cacheKey = (pdbId: string): string => `${STORAGE_PREFIX}${pdbId.toUpperCase()}`;

/** Read the LRU index: most-recently-used PDB IDs first. */
const readIndex = (): string[] => {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
};

const writeIndex = (ids: string[]): void => {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(ids));
  } catch {
    /* quota / disabled — silently no-op */
  }
};

/**
 * Look up a cached PDB by ID. Returns the text on hit and bumps it to the
 * front of the LRU; returns null on miss / schema mismatch.
 */
export const loadPdbCache = (pdbId: string): string | null => {
  const key = cacheKey(pdbId);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedPdb>;
    if (parsed?.v !== SCHEMA_VERSION || typeof parsed.text !== 'string') return null;
    // Bump LRU — move this id to the front of the index.
    const id = pdbId.toUpperCase();
    const idx = readIndex();
    const reordered = [id, ...idx.filter((x) => x !== id)];
    writeIndex(reordered);
    // Touch the entry's timestamp too so diagnostic dumps reflect reality.
    const updated: CachedPdb = { v: SCHEMA_VERSION, id, text: parsed.text, ts: Date.now() };
    try {
      localStorage.setItem(key, JSON.stringify(updated));
    } catch {
      /* if we hit quota writing the timestamp, the read still succeeded */
    }
    return parsed.text;
  } catch {
    return null;
  }
};

/**
 * Cache PDB text under the given ID. Updates the LRU index and evicts the
 * least-recently-used entries when MAX_ENTRIES would be exceeded.
 */
/** Byte size of a cached entry as currently stored (0 if absent/unreadable). */
const entryBytes = (id: string): number => {
  try {
    return localStorage.getItem(cacheKey(id))?.length ?? 0;
  } catch {
    return 0;
  }
};

export const savePdbCache = (pdbId: string, text: string): void => {
  if (!text) return;
  const id = pdbId.toUpperCase();
  const key = cacheKey(id);
  const payload: CachedPdb = { v: SCHEMA_VERSION, id, text, ts: Date.now() };
  const payloadBytes = JSON.stringify(payload).length;
  // A single entry larger than the per-entry soft cap is not worth caching —
  // it would force out everything else. Skip it; the viewer will refetch.
  if (text.length > SOFT_PER_ENTRY_BYTES) {
    console.warn(`[pdbCache] skipping large PDB ${id} (${(text.length / 1024).toFixed(0)} KB) — refetch on demand.`);
    return;
  }
  // Rebuild the LRU with the new entry at the front, then keep only as many
  // entries as fit under BOTH the count cap and the total-byte budget.
  const idx = readIndex();
  const reordered = [id, ...idx.filter((x) => x !== id)];
  const kept: string[] = [];
  let total = payloadBytes; // the new entry counts against the budget too
  for (const otherId of reordered) {
    if (otherId === id) continue; // already accounted for
    if (kept.length + 1 >= MAX_ENTRIES) break;
    const sz = entryBytes(otherId);
    if (total + sz > TOTAL_BUDGET_BYTES) break;
    total += sz;
    kept.push(otherId);
  }
  kept.unshift(id);
  // Evict everything in the old index that didn't make the cut.
  const keptSet = new Set(kept);
  for (const evictedId of reordered) {
    if (keptSet.has(evictedId)) continue;
    try {
      localStorage.removeItem(cacheKey(evictedId));
    } catch {
      /* ignore */
    }
  }
  // Write the new entry. If it exceeds quota, drop the OLDEST kept entry
  // and retry until success or until we've dropped everything else.
  let kept2 = [...kept];
  while (kept2.length > 0) {
    try {
      localStorage.setItem(key, JSON.stringify(payload));
      writeIndex(kept2);
      return;
    } catch {
      // Quota — drop the oldest (last in kept2) and retry.
      const dropped = kept2.pop();
      if (dropped && dropped !== id) {
        try {
          localStorage.removeItem(cacheKey(dropped));
        } catch {
          /* ignore */
        }
      }
    }
  }
  // We tried all evictions — give up silently. The fetch will still
  // succeed at the call site; we just won't cache this one.
};
